import { FC, useEffect, useRef } from 'react';
import { Box, IconButton, Stack, Typography, useTheme } from '@mui/material';
import { BooleanConversation, Conversation } from 'src/services/useChat';
import ReactMarkdown from 'react-markdown';
import { LoadingText } from './LoadingText';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import TopicCard from './TopicCard';
import { useAppDispatch, useAppSelector } from 'src/app/hooks';
import CodeBlock from 'src/components/CodeBlock';
import EditNoteIcon from '@mui/icons-material/EditNote';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { updateCopiedBooleanQuery } from 'src/features/chatNow';
import { useAlert } from 'src/hooks/useAlert';

interface ChatListProps {
  conversation: (Conversation | BooleanConversation)[];
  isLoading?: boolean;
  handleSendMessage: (message: string) => void;
}

const ChatList: FC<ChatListProps> = ({
  conversation,
  isLoading = false,
  handleSendMessage
}) => {
  const theme = useTheme();
  const dispatch = useAppDispatch();
  const { handleClickAlert } = useAlert();
  const { displayName } = useAppSelector((state) => state.users.project);
  const { chatMode } = useAppSelector((state) => state.chatNow);

  const messageEndRef = useRef(null);

  const scrollToBottom = () => {
    if (messageEndRef.current) {
      messageEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const handleCopyAIResponse = (message: string) => {
    navigator.clipboard.writeText(message);
    handleClickAlert({
      message: 'Text has Copied',
      severity: 'success',
      horizontal: 'center',
      vertical: 'top'
    });
  };

  const getResponseText = (
    message: Conversation | BooleanConversation
  ): string => {
    return 'answer' in message ? message.answer : message.response;
  };

  const renderOptions = () => {
    if (chatMode === 'chatNow')
      return (
        <Box
          display="flex"
          gap={1}
          sx={{ maxWidth: '298px', marginBottom: '50px' }}
          flexDirection="column"
        >
          <TopicCard
            description={`What issues related to ${displayName} have appeared on social media in the past month?`}
            onClick={() =>
              handleSendMessage(
                `What issues related to ${displayName} have appeared on social media in the past month?`
              )
            }
          />
          <TopicCard
            description="What topics or issues have been trending on social media over the past week?"
            onClick={() =>
              handleSendMessage(
                `What topics or issues have been trending on social media over the past week?`
              )
            }
          />
          <TopicCard
            description="What social media trends have emerged in the past two weeks?"
            onClick={() =>
              handleSendMessage(
                `What social media trends have emerged in the past two weeks?`
              )
            }
          />
        </Box>
      );
    return (
      <Box
        display="flex"
        gap={1}
        sx={{ maxWidth: '298px', marginBottom: '50px' }}
        flexDirection="column"
      >
        <TopicCard
          description={`Create an Industry Focused Boolean Query`}
          onClick={() =>
            handleSendMessage(`Create an Industry Focused Boolean Query`)
          }
          icon={<EditNoteIcon sx={{ fontSize: '16px', color: '#4B4B4B' }} />}
        />
        <TopicCard
          description="Create a Brand-Focused Boolean Query"
          onClick={() =>
            handleSendMessage(`Create a Brand-Focused Boolean Query`)
          }
          icon={<EditNoteIcon sx={{ fontSize: '16px', color: '#4B4B4B' }} />}
        />
        <TopicCard
          description="Create a Boolean Query from Media Outreach"
          onClick={() =>
            handleSendMessage(`Create a Boolean Query from Media Outreach`)
          }
          icon={<EditNoteIcon sx={{ fontSize: '16px', color: '#4B4B4B' }} />}
        />
      </Box>
    );
  };

  const handleCopyQuery = (message: BooleanConversation) => {
    dispatch(
      updateCopiedBooleanQuery({
        phrasesList: message.include,
        excludePhrases: message.exclude
      })
    );
    handleClickAlert({
      message: 'Boolean query copied successfully',
      severity: 'success',
      horizontal: 'center',
      vertical: 'top'
    });
  };

  const transformIncludeBooleanQueryToString = (arr: string[][]): string => {
    return arr
      .map((innerArr) => {
        if (innerArr.length > 1) {
          return `(${innerArr.map((str) => `"${str}"`).join(' AND ')})`;
        } else {
          return `"${innerArr[0]}"`;
        }
      })
      .join(' OR ');
  };

  const renderQuery = (message: Conversation | BooleanConversation) => {
    if ('include' in message && 'exclude' in message) {
      let query = '';
      query += transformIncludeBooleanQueryToString(message.include);
      if (message.exclude.length > 0) {
        query += ' NOT ';
        query += message.exclude.map((item) => `"${item}"`).join(' OR ');
      }
      return (
        <Stack gap={0.5} alignItems="flex-start">
          <CodeBlock code={query} />
          <IconButton onClick={() => handleCopyQuery(message)}>
            <Stack alignItems="center" spacing={1} direction="row">
              <ContentCopyIcon sx={{ fontSize: '16px', color: '#4B4B4B' }} />
              <Typography fontSize="12px">Copy</Typography>
            </Stack>
          </IconButton>
        </Stack>
      );
    }
  };

  useEffect(() => {
    scrollToBottom();
  }, [isLoading, conversation]);

  return (
    <Box
      sx={{
        width: '100%',
        maxWidth: '800px',
        height: 'calc(100vh - 300px)',
        overflowY: 'auto',
        pt: 2,
        px: 2,
        '&::-webkit-scrollbar': {
          width: '8px'
        },
        '&::-webkit-scrollbar-track': {
          background: '#f1f1f1'
        },
        '&::-webkit-scrollbar-thumb': {
          background: '#888',
          borderRadius: '4px'
        }
      }}
    >
      {conversation.map((message, index) => (
        <Stack key={message.id} gap={3} mb={3}>
          {/* User Message */}
          <Stack direction="row" gap={1}>
            <Box>
              <AccountCircleIcon
                fontSize="medium"
                sx={{
                  color: theme.palette.primary.main,
                  border: '1px solid #BFBFBF',
                  borderRadius: '50%',
                  padding: '3px'
                }}
              />
            </Box>
            <Stack gap={0.5}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                You
              </Typography>
              <Typography variant="subtitle2">{message.question}</Typography>
            </Stack>
          </Stack>
          {/* AI Response */}
          <Stack direction="row" gap={1}>
            <Box>
              <AutoFixHighIcon
                fontSize="medium"
                sx={{
                  color: theme.palette.primary.main,
                  border: '1px solid #BFBFBF',
                  borderRadius: '50%',
                  padding: '3px'
                }}
              />
            </Box>
            <Stack gap={0.5}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                Assistant
              </Typography>
              {isLoading && index === conversation.length - 1 ? (
                <LoadingText />
              ) : (
                <Stack gap={0.5} alignItems="flex-start">
                  <Typography
                    variant="subtitle2"
                    sx={{
                      color: '#2d2d2d'
                    }}
                  >
                    <ReactMarkdown
                      components={{
                        p: ({ children }) => (
                          <Typography variant="subtitle2" gutterBottom>
                            {children}
                          </Typography>
                        )
                      }}
                    >
                      {getResponseText(message)}
                    </ReactMarkdown>
                  </Typography>
                  {chatMode !== 'booleanQuery' && message.id !== '1' && (
                    <IconButton
                      onClick={() =>
                        handleCopyAIResponse(getResponseText(message))
                      }
                    >
                      <Stack alignItems="center" spacing={1} direction="row">
                        <ContentCopyIcon
                          sx={{ fontSize: '16px', color: '#4B4B4B' }}
                        />
                        <Typography fontSize="12px">Copy</Typography>
                      </Stack>
                    </IconButton>
                  )}
                </Stack>
              )}
              {message.id === '1' ? renderOptions() : null}
              {chatMode === 'booleanQuery' && message.id !== '1'
                ? renderQuery(message)
                : null}
            </Stack>
          </Stack>
        </Stack>
      ))}
      <Stack ref={messageEndRef}></Stack>
    </Box>
  );
};

export default ChatList;
